/**
 * Haptic feedback utilities for mobile devices
 */

import { useSettingsStore } from '../stores/settingsStore';

export type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

/**
 * Trigger haptic feedback if supported and enabled
 */
export function triggerHaptic(type: HapticType = 'light'): void {
  // Check if haptic feedback is enabled in settings
  const { hapticFeedback } = useSettingsStore.getState();
  if (!hapticFeedback) return;

  // Try native vibration API
  if ('vibrate' in navigator) {
    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(20);
        break;
      case 'heavy':
        navigator.vibrate(40);
        break;
      case 'success':
        navigator.vibrate([10, 50, 10]); // Short-pause-short
        break;
      case 'warning':
        navigator.vibrate([20, 30, 20, 30, 20]); // Three pulses
        break;
      case 'error':
        navigator.vibrate([50, 30, 50]); // Long-short-long
        break;
    }
  }
}

/**
 * Hook-friendly haptic trigger
 */
export function useHaptics() {
  return {
    light: () => triggerHaptic('light'),
    medium: () => triggerHaptic('medium'),
    heavy: () => triggerHaptic('heavy'),
    success: () => triggerHaptic('success'),
    warning: () => triggerHaptic('warning'),
    error: () => triggerHaptic('error'),
  };
}
